import { api } from "./api";

const MAX_SIDE = 1600;
const MAX_INPUT_BYTES = 25 * 1024 * 1024;

/**
 * Shrink a picked image (camera or library) to at most 1600px on the long side, JPEG ~0.85,
 * on a white background. Keeps uploads around 200–500 KB.
 */
export async function compressImage(file: File): Promise<string> {
  if (!file.type.startsWith("image/")) throw new Error("not_image");
  if (file.size > MAX_INPUT_BYTES) throw new Error("too_large");
  const bitmap = await loadBitmap(file);
  const scale = Math.min(1, MAX_SIDE / Math.max(bitmap.width, bitmap.height));
  const w = Math.max(1, Math.round(bitmap.width * scale));
  const h = Math.max(1, Math.round(bitmap.height * scale));
  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d")!;
  ctx.fillStyle = "#fff";
  ctx.fillRect(0, 0, w, h);
  ctx.drawImage(bitmap as CanvasImageSource, 0, 0, w, h);
  if ("close" in bitmap) (bitmap as ImageBitmap).close();
  return canvas.toDataURL("image/jpeg", 0.85);
}

/**
 * A data: URL (as compressImage returns) as a Blob, decoded right here. Not through fetch(): the site's
 * security policy only lets the page connect to its own server (connect-src 'self'), and data: is refused.
 */
export function dataUrlToBlob(dataUrl: string): Blob {
  const comma = dataUrl.indexOf(",");
  if (!dataUrl.startsWith("data:") || comma < 0) throw new Error("not_data_url");
  const head = dataUrl.slice(5, comma);
  const type = head.split(";")[0] || "application/octet-stream";
  const body = dataUrl.slice(comma + 1);
  if (!head.endsWith(";base64")) return new Blob([decodeURIComponent(body)], { type });
  const bin = atob(body);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return new Blob([bytes], { type });
}

async function loadBitmap(file: File): Promise<ImageBitmap | HTMLImageElement> {
  if ("createImageBitmap" in window) {
    try {
      return await createImageBitmap(file, { imageOrientation: "from-image" } as ImageBitmapOptions);
    } catch {
      /* fall through (e.g. HEIC on some browsers) */
    }
  }
  const url = URL.createObjectURL(file);
  try {
    const img = new Image();
    img.src = url;
    await img.decode();
    return img;
  } finally {
    URL.revokeObjectURL(url);
  }
}

export async function uploadPhoto(dataUrl: string) {
  return api.post<{ id: string; sha256: string }>("/api/photos", { dataUrl });
}
